import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { tokens } from '../styles/tokens.js'

/**
 * Signaturelement: beide Luftmassen auf einer gemeinsamen g/m³-Skala.
 * Der eingefärbte Bereich zwischen den Markern ist die Größe, um die es geht — das Delta.
 */
@customElement('kil-beam')
export class KilBeam extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
      }

      figure {
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .track {
        position: relative;
        height: 5.5rem;
        margin: 0 0.75rem;
      }

      .rail {
        position: absolute;
        inset-inline: 0;
        top: 50%;
        height: 3px;
        translate: 0 -50%;
        background: var(--kil-line-strong);
        border-radius: 2px;
      }

      .band {
        position: absolute;
        top: 50%;
        height: 13px;
        translate: 0 -50%;
        border-radius: 2px;
        background: var(--kil-accent);
        opacity: 0.85;
        transition:
          left var(--kil-motion, 260ms) cubic-bezier(0.2, 0.7, 0.2, 1),
          width var(--kil-motion, 260ms) cubic-bezier(0.2, 0.7, 0.2, 1);
      }

      .marker {
        position: absolute;
        top: 50%;
        width: 2px;
        height: 2.6rem;
        translate: -50% -50%;
        background: var(--kil-ink);
        transition: left var(--kil-motion, 260ms) cubic-bezier(0.2, 0.7, 0.2, 1);
      }

      .marker::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 12px;
        height: 12px;
        translate: -50% -50%;
        rotate: 45deg;
        background: var(--kil-ink);
        border: 2px solid var(--kil-surface);
      }

      /* Außen bleibt hohl, innen ist gefüllt - so sind die beiden Luftmassen
         auch ohne die Beschriftung auseinanderzuhalten. */
      .marker.outdoor::after {
        background: var(--kil-surface);
        border-color: var(--kil-ink);
      }

      .tag {
        position: absolute;
        translate: -50% 0;
        white-space: nowrap;
        text-align: center;
        transition: left var(--kil-motion, 260ms) cubic-bezier(0.2, 0.7, 0.2, 1);
      }

      .tag.above {
        bottom: calc(50% + 1.6rem);
      }

      .tag.below {
        top: calc(50% + 1.6rem);
      }

      .tag .name {
        display: block;
        font-size: 0.6875rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--kil-ink-dim);
      }

      .tag .value {
        display: block;
        font-family: var(--kil-font-mono);
        font-variant-numeric: tabular-nums;
        font-size: 1rem;
        font-weight: 500;
      }

      .delta {
        position: absolute;
        top: 50%;
        translate: -50% -50%;
        padding: 0.1rem 0.45rem;
        border-radius: 999px;
        background: var(--kil-ink);
        color: var(--kil-surface);
        font-family: var(--kil-font-mono);
        font-size: 0.75rem;
        font-weight: 500;
        white-space: nowrap;
        transition: left var(--kil-motion, 260ms) cubic-bezier(0.2, 0.7, 0.2, 1);
      }

      .scale {
        position: relative;
        height: 1.4rem;
        margin: 0 0.75rem;
        border-top: 1px solid var(--kil-line);
      }

      .tick {
        position: absolute;
        top: 0;
        translate: -50% 0;
        padding-top: 0.3rem;
        font-family: var(--kil-font-mono);
        font-size: 0.6875rem;
        color: var(--kil-ink-dim);
      }

      .tick::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 0;
        width: 1px;
        height: 0.25rem;
        background: var(--kil-line-strong);
      }

      figcaption {
        margin: 0 0.75rem;
        font-size: 0.8125rem;
        color: var(--kil-ink-dim);
      }
    `,
  ]

  /** Absolute Feuchte innen in g/m³. */
  @property({ type: Number }) indoor = 0
  /** Absolute Feuchte außen in g/m³. */
  @property({ type: Number }) outdoor = 0

  private get max(): number {
    const highest = Math.max(this.indoor, this.outdoor, 8)
    return Math.ceil((highest * 1.15) / 5) * 5
  }

  private pct(value: number): number {
    return Math.min(100, Math.max(0, (value / this.max) * 100))
  }

  private fmt(value: number): string {
    return value.toFixed(1).replace('.', ',')
  }

  override render() {
    const inPct = this.pct(this.indoor)
    const outPct = this.pct(this.outdoor)
    const left = Math.min(inPct, outPct)
    const width = Math.abs(inPct - outPct)
    const delta = this.indoor - this.outdoor
    const ticks: number[] = []
    for (let value = 0; value <= this.max; value += 5) ticks.push(value)

    return html`
      <figure>
        <div class="track">
          <div class="rail"></div>
          <div class="band" style="left:${left}%; width:${width}%"></div>

          <div class="marker outdoor" style="left:${outPct}%"></div>
          <div class="marker indoor" style="left:${inPct}%"></div>

          <div class="tag above" style="left:${inPct}%">
            <span class="name">innen</span>
            <span class="value">${this.fmt(this.indoor)}</span>
          </div>
          <div class="tag below" style="left:${outPct}%">
            <span class="name">außen</span>
            <span class="value">${this.fmt(this.outdoor)}</span>
          </div>

          ${width > 14
            ? html`<div class="delta" style="left:${left + width / 2}%">
                Δ&thinsp;${this.fmt(Math.abs(delta))}&thinsp;g/m³
              </div>`
            : null}
        </div>

        <div class="scale">
          ${ticks.map(
            (value) => html`<span class="tick" style="left:${this.pct(value)}%">${value}</span>`,
          )}
        </div>

        <figcaption>
          Wassergehalt der Luft in g/m³. Der eingefärbte Bereich ist der Unterschied, um den es
          beim Lüften geht.
        </figcaption>
      </figure>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kil-beam': KilBeam
  }
}
